import React, { useState } from 'react';
import { format } from 'date-fns';
import { useSuspendedSellers, useSuspensionStats, useReviewAppeal } from '@/hooks/useSellerSuspensions';
import { useRemoveSellerSuspension } from '@/hooks/useUser';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const REASON_LABELS: Record<string, string> = {
    fraudulent_listings: 'Fraudulent Listings',
    policy_violations: 'Policy Violations',
    spam: 'Spam',
    scam: 'Scam',
    counterfeit_goods: 'Counterfeit Goods',
    harassment: 'Harassment',
    multiple_reports: 'Multiple Reports',
    other: 'Other',
};

function SellerSuspensionsSection() {
    const [activeTab, setActiveTab] = useState<'all' | 'pending'>('pending');
    const { data: stats, isLoading: statsLoading } = useSuspensionStats();
    const { data: suspensions, isLoading, fetchNextPage, isFetchingNextPage } = useSuspendedSellers(
        activeTab === 'pending' ? 'pending' : undefined
    );
    const reviewAppeal = useReviewAppeal();
    const removeSuspension = useRemoveSellerSuspension();

    // Review appeal modal state
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [selectedSuspension, setSelectedSuspension] = useState<any>(null);
    const [appealResponse, setAppealResponse] = useState('');
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);

    const handleReviewAppeal = (suspension: any, action: 'approve' | 'reject') => {
        setSelectedSuspension(suspension);
        setReviewAction(action);
        setAppealResponse('');
        setReviewModalOpen(true);
    };

    const submitReview = () => {
        if (!selectedSuspension || !reviewAction) return;

        reviewAppeal.mutate({
            suspensionId: selectedSuspension._id,
            data: {
                approved: reviewAction === 'approve',
                appealResponse: appealResponse || (reviewAction === 'approve'
                    ? 'Your appeal has been approved and your marketplace access has been restored.'
                    : 'Your appeal has been reviewed and rejected. The suspension remains in place.'),
            }
        }, {
            onSuccess: () => {
                setReviewModalOpen(false);
                setSelectedSuspension(null);
                setAppealResponse('');
                setReviewAction(null);
            }
        });
    };

    return (
        <main className="w-full overflow-auto px-8 py-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold mb-4">Seller Suspensions</h1>

                {/* Stats Cards */}
                {!statsLoading && stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <Card className="p-4">
                            <div className="text-sm text-neutral-500">Total Suspended</div>
                            <div className="text-2xl font-bold">{stats.total}</div>
                        </Card>
                        <Card className="p-4">
                            <div className="text-sm text-neutral-500">Pending Appeals</div>
                            <div className="text-2xl font-bold text-orange-600">{stats.pendingAppeals}</div>
                        </Card>
                        <Card className="p-4">
                            <div className="text-sm text-neutral-500">Permanent</div>
                            <div className="text-2xl font-bold">{stats.permanent}</div>
                        </Card>
                        <Card className="p-4">
                            <div className="text-sm text-neutral-500">Temporary</div>
                            <div className="text-2xl font-bold">{stats.temporary}</div>
                        </Card>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 mb-4">
                    <Button
                        variant={activeTab === 'pending' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('pending')}
                    >
                        Pending Appeals {stats?.pendingAppeals ? `(${stats.pendingAppeals})` : ''}
                    </Button>
                    <Button
                        variant={activeTab === 'all' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('all')}
                    >
                        All Suspensions
                    </Button>
                </div>
            </div>

            {/* Suspensions List */}
            {isLoading ? (
                <div className="text-center py-8">Loading...</div>
            ) : suspensions.length === 0 ? (
                <div className="text-center py-8 text-neutral-500">
                    {activeTab === 'pending' ? 'No pending appeals' : 'No suspended sellers'}
                </div>
            ) : (
                <div className="space-y-4">
                    {suspensions.map((suspension: any) => (
                        <Card key={suspension._id} className="p-4">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    {/* User Info */}
                                    <div className="flex items-center gap-3 mb-2">
                                        {suspension.userId?.profile ? (
                                            <img
                                                src={suspension.userId.profile}
                                                alt=""
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center">
                                                {suspension.userId?.firstname?.[0] || '?'}
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-medium">
                                                {suspension.userId?.firstname} {suspension.userId?.lastname}
                                            </div>
                                            <div className="text-sm text-neutral-500">
                                                @{suspension.userId?.username || 'unknown'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Suspension Details */}
                                    <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                                        <div>
                                            <span className="text-neutral-500">Reason:</span>{' '}
                                            <span className="font-medium">{REASON_LABELS[suspension.reason] || suspension.reason}</span>
                                        </div>
                                        <div>
                                            <span className="text-neutral-500">Suspended:</span>{' '}
                                            {format(new Date(suspension.createdAt), 'MMM d, yyyy')}
                                        </div>
                                        {suspension.expiresAt && (
                                            <div>
                                                <span className="text-neutral-500">Expires:</span>{' '}
                                                {format(new Date(suspension.expiresAt), 'MMM d, yyyy')}
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-neutral-500">Appeal Status:</span>{' '}
                                            <Badge variant={
                                                suspension.appealStatus === 'pending' ? 'default' :
                                                    suspension.appealStatus === 'rejected' ? 'destructive' :
                                                        'secondary'
                                            }>
                                                {suspension.appealStatus}
                                            </Badge>
                                        </div>
                                    </div>

                                    {suspension.reasonDetails && (
                                        <div className="mt-2 text-sm">
                                            <span className="text-neutral-500">Details:</span>{' '}
                                            {suspension.reasonDetails}
                                        </div>
                                    )}

                                    {/* Appeal Message */}
                                    {suspension.appealStatus !== 'none' && suspension.appealMessage && (
                                        <div className="mt-3 p-3 bg-neutral-100 dark:bg-neutral-800 rounded-md">
                                            <div className="text-sm font-medium mb-1">Appeal Message:</div>
                                            <div className="text-sm">{suspension.appealMessage}</div>
                                            {suspension.appealedAt && (
                                                <div className="text-xs text-neutral-500 mt-1">
                                                    Submitted: {format(new Date(suspension.appealedAt), 'MMM d, yyyy h:mm a')}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Appeal Response (if rejected) */}
                                    {suspension.appealStatus === 'rejected' && suspension.appealResponse && (
                                        <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                                            <div className="text-sm font-medium mb-1 text-red-600">Admin Response:</div>
                                            <div className="text-sm">{suspension.appealResponse}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col gap-2 ml-4">
                                    {suspension.appealStatus === 'pending' && (
                                        <>
                                            <Button
                                                size="sm"
                                                className="bg-green-600 hover:bg-green-700"
                                                onClick={() => handleReviewAppeal(suspension, 'approve')}
                                            >
                                                Approve Appeal
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleReviewAppeal(suspension, 'reject')}
                                            >
                                                Reject Appeal
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => removeSuspension.mutate({ userId: suspension.userId?._id })}
                                    >
                                        Restore Access
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}

                    {/* Load More */}
                    <div className="text-center py-4">
                        <Button
                            variant="outline"
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage}
                        >
                            {isFetchingNextPage ? 'Loading...' : 'Load More'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Review Appeal Modal */}
            <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {reviewAction === 'approve' ? 'Approve Appeal' : 'Reject Appeal'}
                        </DialogTitle>
                        <DialogDescription>
                            {reviewAction === 'approve'
                                ? 'Approving this appeal will restore the seller\'s marketplace access.'
                                : 'Rejecting this appeal will keep the seller suspended.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4">
                        <Label htmlFor="appealResponse">Response to Seller</Label>
                        <Textarea
                            id="appealResponse"
                            placeholder={reviewAction === 'approve'
                                ? 'Your appeal has been approved...'
                                : 'Your appeal has been reviewed and rejected...'}
                            value={appealResponse}
                            onChange={(e) => setAppealResponse(e.target.value)}
                            rows={4}
                            className="mt-2"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submitReview}
                            disabled={reviewAppeal.isPending}
                            className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                            variant={reviewAction === 'reject' ? 'destructive' : 'default'}
                        >
                            {reviewAppeal.isPending ? 'Processing...' : (reviewAction === 'approve' ? 'Approve' : 'Reject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </main>
    );
}

export default React.memo(SellerSuspensionsSection);
